import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Avatar,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { alpha } from "@mui/material/styles";

import {
  COLORS,
  RADIUS,
  PRIORITIES,
  STATUS_COLUMNS,
  departmentTagStyle,
  deptColorOf,
  formatDateInput,
  initialsOf,
} from "./ticketTheme";

const emptyForm = {
  title: "",
  description: "",
  department_id: "",
  priority: "medium",
  due_date: "",
  status: "open",
  assignee_uid: "",
};

/**
 * Same component handles both "New ticket" and "Edit ticket" — the fields
 * are identical in both modes, only the footer (Delete) and the Status
 * field's editability differ.
 *
 * Every ticket on this board is public (visible to its whole department),
 * so there is no visibility toggle here.
 *
 * Props this expects from the parent (TicketsBoard):
 *   open, onClose
 *   mode: "create" | "edit"
 *   departments: [{ id, name, color }]
 *   ticket: existing ticket object, required when mode === "edit" — as
 *     returned by the backend, i.e. flat department_id/department_name and
 *     assignee_id/assignee_name fields, not nested objects.
 *   orgUsers: [{ id, name, last_name, email, profilePictureUrl, dept_ids }]
 *     — the full onboarded+approved org roster, same shape GET
 *     /users/list_users returns (see TeamManagementTable.jsx). The
 *     Assignees picker searches this whole roster by name/email and lets
 *     it be narrowed further by department pill, independent of the
 *     ticket's own Department field.
 *   orgUsersLoading: boolean — shows a loading row in the picker while true.
 *   currentUser: { id, name, email }
 *   onSubmit(payload)              — create
 *   onUpdate(ticketId, changes)    — edit: title/description/department/
 *                                    priority/due_date
 *   onMove(ticketId, status)       — edit: status changed
 *   onAssign(ticketId, assigneeUid)— edit: assignee changed
 *   onDelete(ticketId)             — edit: delete
 */
export default function NewTicketDialog({
  open,
  onClose,
  mode = "create",
  departments = [],
  ticket = null,
  orgUsers = [],
  orgUsersLoading = false,
  currentUser = null,
  visibilityContext = "public",
  onSubmit,
  onUpdate,
  onMove,
  onAssign,
  onDelete,
}) {
  const isEdit = mode === "edit" && !!ticket;
  // Personal tickets are implicitly "assigned to yourself" — there's no
  // one else to hand them to, so the whole Assignees picker is hidden and
  // the creator is wired in automatically instead of being a pickable option.
  const isPersonal = visibilityContext === "personal";

  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Assignee picker state — independent of the form's own Department
  // field, so people can search across every department in the org.
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [assigneeDeptFilter, setAssigneeDeptFilter] = useState("all");

  useEffect(() => {
    if (!open) return;

    setAssigneeSearch("");
    setAssigneeDeptFilter("all");

    if (isEdit) {
      setForm({
        title: ticket.title || "",
        description: ticket.description || "",
        department_id: ticket.department_id || "",
        priority: ticket.priority || "medium",
        due_date: formatDateInput(ticket.due_date),
        status: ticket.status || "open",
        assignee_uid: isPersonal
          ? currentUser?.id || ticket.assignee_id || ""
          : ticket.assignee_id || "",
      });
    } else {
      setForm({
        ...emptyForm,
        assignee_uid: isPersonal ? currentUser?.id || "" : "",
      });
    }
  }, [open, isEdit, ticket, isPersonal, currentUser]);

  // A quick id -> department lookup, so each user's avatar can be colored
  // by their own department, the same color used everywhere else for that
  // department (sidebar dot, filter chip, ticket tag).
  const deptById = useMemo(() => {
    const map = {};
    departments.forEach((d) => {
      map[String(d.id)] = d;
    });
    return map;
  }, [departments]);

  const normalizedUsers = useMemo(
    () =>
      orgUsers.map((u) => {
        const deptIds = (u.dept_ids || []).map(String);
        const primaryDept = deptById[deptIds[0]] || null;
        return {
          id: u.id,
          name:
            `${u.name || ""} ${u.last_name || ""}`.trim() ||
            u.username ||
            u.email ||
            "Unnamed",
          email: u.email || "",
          profilePictureUrl: u.profilePictureUrl || "",
          deptIds,
          deptName: primaryDept?.name || "",
          deptColor: primaryDept ? deptColorOf(primaryDept) : COLORS.accent,
        };
      }),
    [orgUsers, deptById]
  );

  // Visible candidates in the picker: filtered by the selected department
  // pill (if any) and by the free-text search box.
  const assigneeCandidates = useMemo(() => {
    const query = assigneeSearch.trim().toLowerCase();
    return normalizedUsers.filter((u) => {
      const matchesDept =
        assigneeDeptFilter === "all" ||
        u.deptIds.includes(String(assigneeDeptFilter));
      const matchesQuery =
        !query ||
        u.name.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query);
      return matchesDept && matchesQuery;
    });
  }, [normalizedUsers, assigneeDeptFilter, assigneeSearch]);

  // Resolve the selected assignee_uid to a { id, name } to render as a
  // chip. Prefer currentUser / the roster (fresh data), but fall back to
  // the ticket's own denormalized assignee_name from the backend so the
  // chip still renders correctly even if orgUsers hasn't loaded yet.
  const assignee = useMemo(() => {
    if (!form.assignee_uid) return null;
    if (currentUser && form.assignee_uid === currentUser.id) return currentUser;

    const fromRoster = normalizedUsers.find((u) => u.id === form.assignee_uid);
    if (fromRoster) return fromRoster;

    if (isEdit && ticket && ticket.assignee_id === form.assignee_uid) {
      return {
        id: ticket.assignee_id,
        name: ticket.assignee_name || "Unknown",
      };
    }

    return null;
  }, [form.assignee_uid, normalizedUsers, currentUser, isEdit, ticket]);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const canSave = form.title.trim() && form.department_id && !submitting;

  const handleClose = () => {
    if (submitting || deleting) return;
    onClose?.();
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      if (isEdit) {
        const changes = {};
        if (form.title !== ticket.title) changes.title = form.title;
        if (form.description !== (ticket.description || ""))
          changes.description = form.description;
        if (form.department_id !== ticket.department_id) {
          changes.department_id = form.department_id;
        }
        if (form.priority !== ticket.priority) changes.priority = form.priority;
        if (form.due_date !== formatDateInput(ticket.due_date)) {
          changes.due_date = form.due_date || null;
        }

        if (Object.keys(changes).length > 0) {
          await onUpdate?.(ticket.id, changes);
        }
        if (form.status !== ticket.status) {
          await onMove?.(ticket.id, form.status);
        }
        const previousAssignee = ticket.assignee_id || "";
        if (form.assignee_uid !== previousAssignee) {
          await onAssign?.(ticket.id, form.assignee_uid || null);
        }
      } else {
        await onSubmit?.({
          title: form.title,
          description: form.description,
          department_id: form.department_id,
          priority: form.priority,
          due_date: form.due_date || null,
          assignee_uid: form.assignee_uid || null,
        });
      }
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    setDeleting(true);
    try {
      await onDelete?.(ticket.id);
      onClose?.();
    } finally {
      setDeleting(false);
    }
  };

  const fieldLabelSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: COLORS.textSecondary,
    textTransform: "uppercase",
    mb: 0.75,
    display: "block",
  };

  const roundedFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: `${RADIUS.field}px`,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${RADIUS.dialog}px` } }}
    >
      <DialogContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2.5}
        >
          <Typography variant="h6" fontWeight={800}>
            {isEdit ? "Edit ticket" : "New ticket"}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack spacing={2.25}>
          <TextField
            placeholder="Title"
            value={form.title}
            onChange={handleChange("title")}
            autoFocus
            fullWidth
            sx={{
              ...roundedFieldSx,
              "& .MuiOutlinedInput-input": {
                fontSize: 18,
                fontWeight: 700,
                py: 1.4,
              },
            }}
          />

          <TextField
            placeholder="Description"
            value={form.description}
            onChange={handleChange("description")}
            multiline
            minRows={3}
            fullWidth
            sx={roundedFieldSx}
          />

          <Stack direction="row" spacing={2}>
            <Box flex={1}>
              <Typography component="label" sx={fieldLabelSx}>
                Department
              </Typography>
              <TextField
                select
                value={form.department_id}
                onChange={handleChange("department_id")}
                fullWidth
                size="small"
                sx={roundedFieldSx}
              >
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: deptColorOf(dept),
                        }}
                      />
                      <span>{dept.name}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box flex={1}>
              <Typography component="label" sx={fieldLabelSx}>
                Priority
              </Typography>
              <Stack direction="row" spacing={0.75}>
                {PRIORITIES.map((p) => {
                  const active = form.priority === p.value;
                  return (
                    <Box
                      key={p.value}
                      component="button"
                      type="button"
                      onClick={() =>
                        setForm((prev) => ({ ...prev, priority: p.value }))
                      }
                      sx={{
                        flex: 1,
                        border: "1px solid",
                        borderColor: active ? p.color : COLORS.border,
                        bgcolor: active ? p.soft : COLORS.cardBg,
                        color: active ? p.color : COLORS.textSecondary,
                        borderRadius: `${RADIUS.field}px`,
                        py: 1,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                        transition: "all 100ms ease",
                      }}
                    >
                      {p.label}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Box flex={1}>
              <Typography component="label" sx={fieldLabelSx}>
                Due date
              </Typography>
              <TextField
                type="date"
                value={form.due_date}
                onChange={handleChange("due_date")}
                fullWidth
                size="small"
                sx={roundedFieldSx}
              />
            </Box>

            <Box flex={1}>
              <Typography component="label" sx={fieldLabelSx}>
                Status
              </Typography>
              <Tooltip title={isEdit ? "" : "New tickets always start as Open"}>
                <span>
                  <TextField
                    select
                    value={form.status}
                    onChange={handleChange("status")}
                    fullWidth
                    size="small"
                    disabled={!isEdit}
                    sx={roundedFieldSx}
                  >
                    {STATUS_COLUMNS.map((s) => (
                      <MenuItem key={s.key} value={s.key}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </span>
              </Tooltip>
            </Box>
          </Stack>

          <Box>
            <Typography component="label" sx={fieldLabelSx}>
              Assignee
            </Typography>

            {isPersonal ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  border: "1px solid",
                  borderColor: COLORS.border,
                  bgcolor: COLORS.pageBg,
                  borderRadius: `${RADIUS.pill}px`,
                  pl: 0.5,
                  pr: 1.25,
                  py: 0.375,
                }}
              >
                <Avatar
                  src={currentUser?.profilePictureUrl}
                  sx={{
                    width: 20,
                    height: 20,
                    fontSize: 10,
                    bgcolor: COLORS.accent,
                  }}
                >
                  {initialsOf(currentUser?.name)}
                </Avatar>
                <Typography variant="caption" fontWeight={600}>
                  {currentUser?.name || "You"} (personal ticket)
                </Typography>
              </Box>
            ) : (
              <>
                {assignee && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      border: "1px solid",
                      borderColor: COLORS.border,
                      borderRadius: `${RADIUS.pill}px`,
                      pl: 0.5,
                      pr: 1,
                      py: 0.375,
                      mb: 1,
                    }}
                  >
                    <Avatar
                      src={assignee.profilePictureUrl}
                      sx={{
                        width: 20,
                        height: 20,
                        fontSize: 10,
                        bgcolor: assignee.deptColor || COLORS.accent,
                      }}
                    >
                      {initialsOf(assignee.name)}
                    </Avatar>
                    <Typography variant="caption" fontWeight={600}>
                      {assignee.name}
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{ p: 0.25 }}
                      onClick={() =>
                        setForm((prev) => ({ ...prev, assignee_uid: "" }))
                      }
                    >
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                )}

                <TextField
                  placeholder="Search team..."
                  value={assigneeSearch}
                  onChange={(e) => setAssigneeSearch(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ ...roundedFieldSx, mb: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          fontSize="small"
                          sx={{ color: COLORS.textDisabled }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 1 }}
                >
                  <DeptPill
                    active={assigneeDeptFilter === "all"}
                    label="All"
                    onClick={() => setAssigneeDeptFilter("all")}
                  />
                  {departments.map((dept) => (
                    <DeptPill
                      key={dept.id}
                      active={assigneeDeptFilter === dept.id}
                      color={deptColorOf(dept)}
                      label={dept.name}
                      onClick={() => setAssigneeDeptFilter(dept.id)}
                    />
                  ))}
                </Stack>

                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: COLORS.border,
                    borderRadius: `${RADIUS.field}px`,
                    maxHeight: 190,
                    overflowY: "auto",
                  }}
                >
                  {orgUsersLoading ? (
                    <Typography
                      variant="body2"
                      sx={{ p: 1.5, color: COLORS.textDisabled }}
                    >
                      Loading team members…
                    </Typography>
                  ) : assigneeCandidates.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{ p: 1.5, color: COLORS.textDisabled }}
                    >
                      No matching team members
                    </Typography>
                  ) : (
                    assigneeCandidates.map((u) => {
                      const selected = u.id === form.assignee_uid;
                      return (
                        <Box
                          key={u.id}
                          component="button"
                          type="button"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              assignee_uid: selected ? "" : u.id,
                            }))
                          }
                          sx={{
                            display: "flex",
                            width: "100%",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            border: "none",
                            borderBottom: "1px solid",
                            borderColor: COLORS.border,
                            bgcolor: selected
                              ? COLORS.accentSoft
                              : "transparent",
                            cursor: "pointer",
                            textAlign: "left",
                            "&:last-of-type": { borderBottom: "none" },
                            "&:hover": {
                              bgcolor: selected
                                ? COLORS.accentSoft
                                : alpha(COLORS.accent, 0.06),
                            },
                          }}
                        >
                          <Avatar
                            src={u.profilePictureUrl}
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: 12,
                              bgcolor: u.deptColor || COLORS.accent,
                            }}
                          >
                            {initialsOf(u.name)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              noWrap
                              sx={{ color: COLORS.textPrimary }}
                            >
                              {u.name}
                            </Typography>
                            {u.deptName && (
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                  display: "block",
                                  color: COLORS.textSecondary,
                                }}
                              >
                                {u.deptName}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </>
            )}
          </Box>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mt={3.5}
        >
          <Box>
            {isEdit && (
              <Button
                onClick={handleDelete}
                disabled={deleting || submitting}
                sx={{
                  color: COLORS.danger,
                  border: "1px solid",
                  borderColor: COLORS.dangerSoft,
                  bgcolor: COLORS.dangerSoft,
                  borderRadius: `${RADIUS.field}px`,
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": { bgcolor: "#FCDCDC" },
                }}
              >
                Delete
              </Button>
            )}
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={handleClose}
              disabled={submitting || deleting}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: COLORS.textSecondary,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!canSave}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: `${RADIUS.field}px`,
                bgcolor: COLORS.accent,
                px: 3,
                "&:hover": { bgcolor: "#5B49D6" },
              }}
            >
              {isEdit ? "Save changes" : "Create ticket"}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

/** Department pill used in the Assignees search filter — always tinted
 * with that department's own color (like the tags on ticket cards),
 * with a solid border added to show which one is currently selected. */
function DeptPill({ active, color, label, onClick }) {
  if (!color) {
    // "All" pill has no department color of its own.
    return (
      <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          border: "1px solid",
          borderColor: active ? COLORS.segmentActiveBg : COLORS.border,
          bgcolor: active ? COLORS.segmentActiveBg : COLORS.cardBg,
          color: active ? "#fff" : COLORS.textSecondary,
          borderRadius: `${RADIUS.field}px`,
          px: 1.25,
          py: 0.5,
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          whiteSpace: "nowrap",
          transition: "all 100ms ease",
        }}
      >
        {label}
      </Box>
    );
  }

  const tint = departmentTagStyle(color);
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        border: "1px solid",
        borderColor: active ? tint.color : "transparent",
        bgcolor: tint.bg,
        color: tint.color,
        borderRadius: `${RADIUS.field}px`,
        px: 1.25,
        py: 0.5,
        fontSize: 12,
        fontWeight: 700,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "all 100ms ease",
      }}
    >
      {label}
    </Box>
  );
}
